import { useEffect, useState } from 'react';

import { isNewBuild, VERSION_CHECK_INTERVAL_MS } from './build-version.logic.js';

/**
 * Whether a build newer than this tab's is already being served.
 *
 * THE GAP THIS CLOSES. `/assets/*` is immutable and `index.html` is `no-store`,
 * so a tab that RELOADS after a deploy always gets the new build — and the
 * ErrorBoundary already forces that reload when a `lazy()` chunk 404s. What
 * neither covers is the tab that does not reload and does not need a new chunk:
 * it sits on a route whose code is already in memory, and goes on calling an API
 * that has moved underneath it.
 *
 * That is not hypothetical. A console tab held a build from three deploys back
 * and spent the afternoon sending `sort=name` to an endpoint whose sort keys had
 * changed (400) and mapping over a `/masterdata/channels` response that had
 * become a paged envelope — which threw, and took the page with it. Every
 * request it made was answered correctly. The client was simply old.
 *
 * PRODUCTION ONLY. `version.json` is emitted by the build (see vite.config.ts),
 * so in dev there is nothing to fetch and nothing to compare — `import.meta.env`
 * is statically replaced, so this also drops the whole body from the dev bundle.
 *
 * ON VISIBILITY, NOT ON A TIMER. The tab this exists for is one left open behind
 * other work; it learns it is stale at the moment somebody looks at it again,
 * which is the moment before they click something. A background interval would
 * spend requests on a tab nobody is reading.
 */
export function useBuildVersion(): boolean {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!import.meta.env.PROD || stale) return;

    const controller = new AbortController();
    let lastCheck = 0;

    const check = async (): Promise<void> => {
      const now = Date.now();
      if (now - lastCheck < VERSION_CHECK_INTERVAL_MS) return;
      lastCheck = now;

      try {
        /*
         * `no-store` on the request as well as on the response.
         *
         * nginx says `no-store` for this path, but a tab that has been open for
         * hours may hold an entry from before that header existed — and this is
         * exactly the tab this whole mechanism is for. Asking the cache to stay
         * out of it costs nothing and does not depend on the server.
         */
        const response = await fetch('/version.json', {
          cache: 'no-store',
          signal: controller.signal,
        });
        if (!response.ok) return;
        if (isNewBuild(__FV_BUILD__, await response.json())) setStale(true);
      } catch {
        /*
         * SILENT, AND THAT IS THE POINT. This fetch fails for a dropped link, an
         * aborted check on unmount, and a container mid-restart — the same
         * conditions that produced the console log this feature came from, where
         * a transient failure was reported to the user as something else
         * entirely. "Cannot reach the server" is not evidence of a new version.
         */
      }
    };

    void check();

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void check();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      controller.abort();
    };
  }, [stale]);

  return stale;
}

declare global {
  /**
   * The build id compiled in by `vite.config.ts`. Declared here rather than in a
   * shared ambient file for the same reason `__FV_CONFIG__` is declared in
   * `runtime-config.ts`: the declaration belongs beside its only reader.
   */
  const __FV_BUILD__: string;
}
